import moment from 'moment-timezone';
import { isValidNumber } from './validation';

export const SHOWING_TIMEZONE = 'America/New_York';

const normalizeNoticeWindowDays = (noticeWindowInDays) => {
    const numericNoticeWindow = Number(noticeWindowInDays);
    if (!Number.isFinite(numericNoticeWindow) || numericNoticeWindow < 0) {
        return null;
    }
    return numericNoticeWindow;
};

export const formatDate = (dateObj) => {
    // Extract date parts
    const options = { year: 'numeric', month: 'short', day: 'numeric' };
    const formattedDate = dateObj.toLocaleDateString('en-US', options);

    // Extract the hours and minutes
    let hours = dateObj.getHours();
    const minutes = dateObj.getMinutes();

    // Convert 24-hour time to 12-hour time format
    const ampm = hours >= 12 ? 'PM' : 'AM';
    hours = hours % 12;
    hours = hours ? hours : 12; // the hour '0' should be '12'

    // Format the time parts, ensuring two digits
    const formattedTime = `${hours}:${minutes.toString().padStart(2, '0')} ${ampm}`;

    return { formattedDate, formattedTime };
};

const SHOWING_DATE_TIME_FORMATS = [
    'MMM D, YYYY h:mm A z',
    'MMM DD, YYYY h:mm A z',
    'MMM D, YYYY hh:mm A z',
    'MMM DD, YYYY hh:mm A z',
    'MMM D, YYYY h:mm A',
    'MMM DD, YYYY h:mm A',
    'MMM D, YYYY hh:mm A',
    'MMM DD, YYYY hh:mm A',
    'YYYY-MM-DD HH:mm:ss',
    'YYYY-MM-DDTHH:mm:ss',
    'YYYY-MM-DD HH:mm',
    'YYYY-MM-DDTHH:mm',
    moment.ISO_8601,
];

export const parseDateTimeInTimezone = (value, timezoneName = SHOWING_TIMEZONE) => {
    if (value === null || value === undefined) return null;
    const raw = String(value).trim();
    if (!raw) return null;

    const hasOffset = /([zZ]|[+-]\d{2}:?\d{2})$/.test(raw);
    const hasTrailingTimezoneAbbr = /\b[A-Z]{2,5}$/.test(raw) && !/[AP]M$/i.test(raw);

    let parsed = null;
    if (hasOffset) {
        parsed = moment.parseZone(raw);
    } else if (hasTrailingTimezoneAbbr) {
        parsed = moment.tz(raw, SHOWING_DATE_TIME_FORMATS, true, timezoneName || SHOWING_TIMEZONE);
    } else {
        parsed = moment.tz(raw, SHOWING_DATE_TIME_FORMATS, timezoneName || SHOWING_TIMEZONE);
    }

    if (!parsed?.isValid()) {
        const loose = moment(raw);
        if (loose.isValid()) {
            parsed = loose;
        }
    }

    return parsed?.isValid() ? parsed.tz(timezoneName || SHOWING_TIMEZONE) : null;
};

export const formatDateTimeWithTimezone = (
    value,
    timezoneName = SHOWING_TIMEZONE,
    outputFormat = 'MMM DD, YYYY h:mm A z',
    fallback = '-'
) => {
    const parsed = parseDateTimeInTimezone(value, timezoneName);
    return parsed?.isValid() ? parsed.format(outputFormat) : fallback;
};

// export function generateNextDates() {
//     const dates = [];
//     for (let i = 0; i < 9; i++) {
//         let date = new Date();
//         date.setDate(date.getDate() + i);
//         dates.push(date);
//     }
//     return dates;
// }

export function generateNextDates(
    isVacant = false,
    is24window = false,
    noticeWindowInDays,
    showingTimezone = SHOWING_TIMEZONE
) {
    const dates = [];
    const nowInShowingTz = moment.tz(showingTimezone || SHOWING_TIMEZONE);
    const startOfTodayInShowingTz = nowInShowingTz.clone().startOf('day');

    const normalizedNoticeWindowInDays = normalizeNoticeWindowDays(noticeWindowInDays);

    // Determine the offset
    let startOffset;
    if (isVacant) {
        startOffset = 0;
    } else if (normalizedNoticeWindowInDays !== null) {
        const noticeWindowInHours = normalizedNoticeWindowInDays * 24;
        const earliestAllowedMoment = nowInShowingTz.clone().add(noticeWindowInHours, 'hours');
        startOffset = Math.max(
            earliestAllowedMoment.startOf('day').diff(startOfTodayInShowingTz, 'days'),
            0
        );
    } else if (is24window) {
        startOffset = 1;
    } else {
        startOffset = 2;
    }
    for (let i = 0; i < 9; i++) {
        const date = startOfTodayInShowingTz
            .clone()
            .add(i + startOffset, 'days')
            .hour(12)
            .minute(0)
            .second(0)
            .millisecond(0)
            .toDate();
        dates.push(date);
    }

    return dates;
}

const EXTENDED_HOURS_CLIENT_IDS = ['miguez_real_estate_consulting', 'oconnorandhighland'];
const isExtendedHoursClient = (clientId) =>
    !!clientId && EXTENDED_HOURS_CLIENT_IDS.includes(String(clientId).toLowerCase());

export function generateTimeSlots(
    isVacant = false,
    activeDate,
    is24window = false,
    noticeWindowInDays,
    showingTimezone = SHOWING_TIMEZONE,
    clientId = null
) {
    const slots = [];
    const startTime = 10; // 10 AM
    const endTime = isExtendedHoursClient(clientId) ? 20 : 18.5; // 8 PM for select clients, else 6:30 PM

    const normalizedNoticeWindowInDays = normalizeNoticeWindowDays(noticeWindowInDays);
    const normalizedNoticeWindowInHours =
        normalizedNoticeWindowInDays !== null ? normalizedNoticeWindowInDays * 24 : null;
    const nowInShowingTz = moment.tz(showingTimezone || SHOWING_TIMEZONE);
    const minimumAllowed =
        normalizedNoticeWindowInDays !== null
            ? nowInShowingTz.clone().add(normalizedNoticeWindowInHours, 'hours')
            : is24window
              ? nowInShowingTz.clone().add(24, 'hours')
              : nowInShowingTz.clone().add(48, 'hours');
    const activeDateMoment = moment.tz(
        activeDate,
        ['MMM D, YYYY', 'MMM DD, YYYY', 'YYYY-MM-DD'],
        showingTimezone || SHOWING_TIMEZONE
    );
    if (!activeDateMoment.isValid()) {
        return slots;
    }
    const isToday = activeDateMoment.isSame(nowInShowingTz, 'day');

    for (let hours = startTime; hours <= endTime; ) {
        let hourPart = Math.floor(hours);
        let minutePart = (hours - hourPart) * 60;

        let period = hourPart >= 12 ? 'PM' : 'AM';
        let displayHour = hourPart % 12 === 0 ? 12 : hourPart % 12;
        let displayMinute = minutePart === 0 ? '00' : minutePart;

        let time = `${displayHour}:${displayMinute} ${period}`;
        const slotMoment = activeDateMoment
            .clone()
            .hour(hourPart)
            .minute(minutePart)
            .second(0)
            .millisecond(0);

        if (!isVacant) {
            // If not vacant, include all slots
            if (slotMoment.isAfter(minimumAllowed)) {
                slots.push(time);
            }
        } else {
            if (isToday) {
                // If vacant and today, include only future slots
                if (slotMoment.isAfter(nowInShowingTz)) {
                    slots.push(time);
                }
            } else {
                // If vacant but not today, include only the time slots 48 hours in the future from now
                slots.push(time);
            }
        }

        hours += 0.25; // increment by 15 minutes
    }

    return slots;
}

export function checkDateRequirement(obj, isVacant, min_slots_required = 2, min_days_required = 1) {
    if (isVacant) {
        // Vacant: At least 2 dates with at least 1 time slot each
        return (
            Object.keys(obj).length >= 1 &&
            Object.entries(obj).every(([key, value]) => Array.isArray(value) && value.length >= 1)
        );
    } else {
        return (
            Object.keys(obj).length >= Number(min_days_required) &&
            Object.entries(obj).every(
                ([key, value]) => Array.isArray(value) && value.length >= Number(min_slots_required)
            )
        );
    }
}

export const colorArray = [
    '#346751', // Emerald Green
    '#F1C40F', // Vivid Yellow
    '#3498DB', // Bright Blue
    '#E74C3C', // Strong Coral
    '#9B59B6', // Amethyst Purple
    '#16A085', // Sea Green
    '#2980B9', // Lively Ocean Blue
    '#E67E22', // Carrot Orange
    '#2ECC71', // Neon Green
    '#D35400', // Pumpkin Orange
];

export function removeSlashes(inputString) {
    return inputString.replace(/\//g, '');
}

export const toProperCase = (str) => {
    return str.replace(/\w\S*/g, (txt) => {
        return txt.charAt(0).toUpperCase() + txt.substr(1).toLowerCase();
    });
};

export const formatTrustTagLabel = (value) => {
    if (!value) return '';
    return String(value)
        .trim()
        .replace(/[_-]+/g, ' ')
        .replace(/\s+/g, ' ')
        .toLowerCase()
        .replace(/\b\w/g, (char) => char.toUpperCase());
};

const TRUST_PRAISE_TAGS = new Set([
    'professional',
    'professionalism',
    'friendly',
    'helpful',
    'punctual',
    'respectful',
    'clear_communication',
    'great_communication',
    'on_time',
    'prepared',
    'knowledgeable',
]);

export const isTrustPraiseTag = (value) => {
    if (!value) return false;
    const normalized = String(value)
        .trim()
        .toLowerCase()
        .replace(/[\s-]+/g, '_');
    return normalized.startsWith('praise_') || TRUST_PRAISE_TAGS.has(normalized);
};

export const toPercentage = (num) => {
    return num * 100;
};

export function formatCurrency(number) {
    return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD',
        minimumFractionDigits: 0,
    }).format(Number(number));
}

// const formatCurrency = (value) => {
//     // Convert the input to a number and format it as currency
//     const numberValue = Number(value);
//     return numberValue.toLocaleString('en-US', {
//         style: 'currency',
//         currency: 'USD',
//     });
// };

export function formatNumber(num) {
    return String(parseFloat(num));
}

export function convertDates(showingTrendData) {
    // Loop over each key in the showingTrendData object ('weekly' and 'monthly')
    Object.keys(showingTrendData).forEach((timeFrame) => {
        // Loop over each category (like 'Requested', 'Cancelled', 'Completed')
        showingTrendData[timeFrame].forEach((categoryData, index) => {
            // Assign a color from the colorArray based on the index
            // Ensure the index wraps around if there are more categories than colors
            categoryData.color = colorArray[index % colorArray.length];

            // Loop over each value entry
            categoryData.values.forEach((dataEntry) => {
                // Convert the date string to a Date object
                dataEntry.date = new Date(dataEntry.date);
            });
        });
    });
    return showingTrendData;
}

export function compareDateTime(dateTime) {
    const currentDate = new Date();
    const inputDate = new Date(dateTime);

    // Calculate the time difference in milliseconds
    const timeDifference = currentDate - inputDate;

    // Calculate time units
    const minutes = Math.floor(timeDifference / (1000 * 60));
    const hours = Math.floor(timeDifference / (1000 * 60 * 60));
    const days = Math.floor(timeDifference / (1000 * 60 * 60 * 24));
    const months = Math.floor(timeDifference / (1000 * 60 * 60 * 24 * 30)); // Approximation for a month

    if (minutes < 60) {
        return minutes + 'min(s) ago';
    } else if (hours < 24) {
        return hours + 'hr(s) ago';
    } else {
        return days + 'd ago';
        // } else {
        //   return months + ' month(s) ago';
        // }
    }
}

export const debounce = (func, wait) => {
    let timeout;
    return function executedFunction(...args) {
        const later = () => {
            clearTimeout(timeout);
            func(...args);
        };
        clearTimeout(timeout);
        timeout = setTimeout(later, wait);
    };
};

export function throttle(func, limit) {
    let inThrottle;
    return function () {
        const args = arguments;
        const context = this;
        if (!inThrottle) {
            func.apply(context, args);
            inThrottle = true;
            setTimeout(() => (inThrottle = false), limit);
        }
    };
}

export function throttleAndDebounce(func, throttleMs, debounceMs) {
    let lastFunc, lastRan;
    return function () {
        const context = this;
        const args = arguments;
        if (!lastRan) {
            func.apply(context, args);
            lastRan = Date.now();
        } else {
            clearTimeout(lastFunc);
            lastFunc = setTimeout(
                function () {
                    if (Date.now() - lastRan >= throttleMs) {
                        func.apply(context, args);
                        lastRan = Date.now();
                    }
                },
                Math.max(throttleMs - (Date.now() - lastRan), debounceMs)
            );
        }
    };
}

export const allDocs = [
    'passport',
    'photo id',
    'i20',
    'visa',
    'proof of funds',
    'admit letter',
    'credit report',
    'credit score',
    'pay stub(1)',
    'pay stub(2)',
    'offer letter',
];

export const baseDocs = {
    'International Student': new Set(['passport', 'i20', 'visa', 'proof of funds', 'admit letter']),
    Student: new Set(['photo id', 'admit letter']),
    'Working Professional': new Set([
        'photo id',
        'credit report',
        'credit score',
        'pay stub(1)',
        'pay stub(2)',
    ]),
    Family: new Set(['photo id', 'credit report', 'pay stub(1)', 'pay stub(2)']),
};

export const cosignerDocs = [
    'photo id',
    'bank statement',
    'w2 form',
    'credit report',
    'credit score',
    'pay stub(1)',
    'pay stub(2)',
];

export const preSelectedCosignerDocs = ['photo id', 'credit report'];

export const dateDiff = (dateBegin, dateEnd) => {
    const date1 = new Date(dateBegin);
    const date2 = new Date(dateEnd);

    // Initialize difference variables
    let months = 0;
    let days = 0;

    // Calculate difference in months
    months = (date2.getFullYear() - date1.getFullYear()) * 12;
    months -= date1.getMonth();
    months += date2.getMonth();

    // Calculate days difference (inclusive of both start and end dates)
    if (date2.getDate() < date1.getDate()) {
        months--;
        const newDate2 = new Date(date2.getFullYear(), date2.getMonth(), 0); // Last day of the previous month of date2
        days = newDate2.getDate() - date1.getDate() + date2.getDate() + 1;
    } else {
        days = date2.getDate() - date1.getDate() + 1;
    }

    return { months, days };
};

export function camelCaseToTitle(camelCase) {
    // Split the string at each point where an uppercase letter follows a lowercase letter
    const words = camelCase.split(/(?<=[a-z])(?=[A-Z])/);

    // Capitalize the first letter of each word and join them back with spaces
    const title = words.map((word) => word.charAt(0).toUpperCase() + word.slice(1)).join(' ');

    return title;
}

// const title = camelCaseToTitle("cityStateZip");
// console.log(title); // "City State Zip"

const sortDateValues = (values) => {
    return values.sort((a, b) => new Date(a.date) - new Date(b.date));
};

export const sortTrendData = (trendData) => {
    return trendData.map((category) => ({
        ...category,
        values: sortDateValues(category.values),
    }));
};

export const formatAddress = (address) => {
    return address
        .toLowerCase() // Convert to lowercase
        .replace(/\s+/g, '-') // Replace spaces with hyphens
        .replace(/[.#]/g, ''); // Remove '.' and '#'
};

export const getColorsForCharts = (state) => {
    const status = state.toLowerCase();
    if (status == 'completed') return '#1CCB00';
    if (status == 'cancelled') return '#E74C3C';
    if (status == 'agent cancelled') return '#E74C3C';
    if (status == 'tenant cancelled') return '#E74C3C';
    if (status == 'declined') return '#D35400';
    if (status == 'requested') return '#F1C40F';
    if (status == 'accepted') return '#16A085';
    if (status == 'total') return '#3498DB';
    if (status == 'upcoming') return '#6AA0CC';
    if (status == 'on hold') return '#9B59B6';
    if (status == 'new') return '#00796B';
    if (status == 'rescheduled') return '#FFA500';
    if (status == 'no response') return '#000000';
};

export const checkIsValidEmail = (email) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

export const formatDateTimeForShowingPayload = (dateInput) => {
    const localMoment = moment(dateInput);
    if (!localMoment.isValid()) {
        return '';
    }

    // Preserve the selected wall-clock date/time, then interpret it in showing TZ.
    const showingTzMoment = moment.tz(
        {
            year: localMoment.year(),
            month: localMoment.month(),
            day: localMoment.date(),
            hour: localMoment.hour(),
            minute: localMoment.minute(),
            second: 0,
            millisecond: 0,
        },
        SHOWING_TIMEZONE
    );

    return showingTzMoment.format('MMM DD, YYYY h:mm A');
};

export const validatePassword = (password) => {
    // Check if password is provided
    if (!password) return false;

    // Validate password requirements
    const minLength = password.length >= 8;
    const hasLowerCase = /[a-z]/.test(password);
    const hasUpperCase = /[A-Z]/.test(password);
    const hasDigit = /\d/.test(password);
    const hasSpecialChar = /[@$!%*?&]/.test(password);

    // Return true only if all requirements are met
    return minLength && hasLowerCase && hasUpperCase && hasDigit && hasSpecialChar;
};
export const getWeekDateRanges = () => {
    const now = new Date();
    const currentDay = now.getDay(); // 0 = Sunday, 1 = Monday, etc.

    // Calculate start of current week (Sunday)
    const startOfThisWeek = new Date(now);
    startOfThisWeek.setDate(now.getDate() - currentDay);
    startOfThisWeek.setHours(0, 0, 0, 0);

    // Calculate end of current week (Saturday)
    const endOfThisWeek = new Date(startOfThisWeek);
    endOfThisWeek.setDate(startOfThisWeek.getDate() + 6);
    endOfThisWeek.setHours(23, 59, 59, 999);

    // Calculate start of last week
    const startOfLastWeek = new Date(startOfThisWeek);
    startOfLastWeek.setDate(startOfThisWeek.getDate() - 7);

    // Calculate end of last week
    const endOfLastWeek = new Date(endOfThisWeek);
    endOfLastWeek.setDate(endOfThisWeek.getDate() - 7);

    // Calculate start of current month
    const startOfThisMonth = new Date(now.getFullYear(), now.getMonth(), 1);
    startOfThisMonth.setHours(0, 0, 0, 0);

    // Calculate end of current month
    const endOfThisMonth = new Date(now.getFullYear(), now.getMonth() + 1, 0);
    endOfThisMonth.setHours(23, 59, 59, 999);

    // Calculate start of last month
    const startOfLastMonth = new Date(now.getFullYear(), now.getMonth() - 1, 1);
    startOfLastMonth.setHours(0, 0, 0, 0);

    // Calculate end of last month
    const endOfLastMonth = new Date(now.getFullYear(), now.getMonth(), 0);
    endOfLastMonth.setHours(23, 59, 59, 999);

    return {
        thisWeek: {
            start_date: startOfThisWeek.toISOString().split('T')[0], // YYYY-MM-DD format
            end_date: endOfThisWeek.toISOString().split('T')[0],
        },
        lastWeek: {
            start_date: startOfLastWeek.toISOString().split('T')[0],
            end_date: endOfLastWeek.toISOString().split('T')[0],
        },
        thisMonth: {
            start_date: startOfThisMonth.toISOString().split('T')[0],
            end_date: endOfThisMonth.toISOString().split('T')[0],
        },
        lastMonth: {
            start_date: startOfLastMonth.toISOString().split('T')[0],
            end_date: endOfLastMonth.toISOString().split('T')[0],
        },
    };
};

export function getRelativeTime(date) {
    const now = moment();
    const tourDate = moment(date);

    if (!tourDate.isValid()) return 'Unknown';

    const diffInMinutes = now.diff(tourDate, 'minutes');
    const diffInHours = now.diff(tourDate, 'hours');
    const diffInDays = now.diff(tourDate, 'days');
    const diffInWeeks = now.diff(tourDate, 'weeks');
    const diffInMonths = now.diff(tourDate, 'months');

    if (diffInMinutes < 1) return 'Just now';
    if (diffInMinutes < 60) return `${diffInMinutes} min ago`;
    if (diffInHours < 24) return `${diffInHours} hour${diffInHours > 1 ? 's' : ''} ago`;
    if (diffInDays === 1) return 'Yesterday';
    if (diffInDays === 0) return 'Today';
    if (diffInWeeks < 2) return 'Last week';
    if (diffInMonths < 2) return 'Last month';

    return tourDate.format('MMM DD, YYYY');
}

export const getFormattedTodaysDate = () => {
    return moment().format('DD MMM YY');
};

// Today in the YYYY-MM-DD format an <input type="date"> expects, in local time.
export const getTodayInputDate = () => moment().format('YYYY-MM-DD');

// Tomorrow in the YYYY-MM-DD format an <input type="date"> expects, in local time.
export const getTomorrowInputDate = () => moment().add(1, 'day').format('YYYY-MM-DD');

export const getDisplayYearDashboard = () => {
    const currentYear = new Date().getFullYear();
    const displayDate =
        localStorage.getItem(`year-${currentYear}`) === currentYear.toString()
            ? getFormattedTodaysDate()
            : `Year: ${localStorage.getItem(`year-${currentYear}`)}`;

    return displayDate;
};

// Function to get quarters backwards from current month
export const getQuartersBackwards = () => {
    const currentDate = moment();
    const quarters = [];

    // Determine current quarter
    const currentMonth = currentDate.month() + 1; // moment months are 0-indexed
    let currentQuarter;

    if (currentMonth >= 1 && currentMonth <= 3) {
        currentQuarter = 1;
    } else if (currentMonth >= 4 && currentMonth <= 6) {
        currentQuarter = 2;
    } else if (currentMonth >= 7 && currentMonth <= 9) {
        currentQuarter = 3;
    } else {
        currentQuarter = 4;
    }

    let year = currentDate.year();
    let quarter = currentQuarter;

    // Generate 4 quarters backwards from current quarter
    for (let i = 0; i < 4; i++) {
        let startMonth, endMonth, startDay, endDay, label;

        switch (quarter) {
            case 1:
                startMonth = 1;
                endMonth = 3;
                startDay = 1;
                endDay = 31;
                label = `Jan-Mar ${year}`;
                break;
            case 2:
                startMonth = 4;
                endMonth = 6;
                startDay = 1;
                endDay = 30;
                label = `Apr-Jun ${year}`;
                break;
            case 3:
                startMonth = 7;
                endMonth = 9;
                startDay = 1;
                endDay = 30;
                label = `Jul-Sep ${year}`;
                break;
            case 4:
                startMonth = 10;
                endMonth = 12;
                startDay = 1;
                endDay = 31;
                label = `Oct-Dec ${year}`;
                break;
        }

        quarters.push({
            label: label,
            startDate: `${year}-${startMonth}-${startDay}`,
            endDate: `${year}-${endMonth}-${endDay}`,
            quarter: quarter,
            year: year,
        });

        // Move to previous quarter
        quarter--;
        if (quarter < 1) {
            quarter = 4;
            year--;
        }
    }

    return quarters;
};

export const hexToHsl = (hex) => {
    const r = parseInt(hex.slice(1, 3), 16) / 255;
    const g = parseInt(hex.slice(3, 5), 16) / 255;
    const b = parseInt(hex.slice(5, 7), 16) / 255;

    const max = Math.max(r, g, b);
    const min = Math.min(r, g, b);
    let h,
        s,
        l = (max + min) / 2;

    if (max === min) {
        h = s = 0;
    } else {
        const d = max - min;
        s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
        switch (max) {
            case r:
                h = (g - b) / d + (g < b ? 6 : 0);
                break;
            case g:
                h = (b - r) / d + 2;
                break;
            case b:
                h = (r - g) / d + 4;
                break;
        }
        h /= 6;
    }

    return `${Math.round(h * 360)} ${Math.round(s * 100)}% ${Math.round(l * 100)}%`;
};

export const buildConcessionMeaningText = (concession) => {
    if (!concession) return '';

    const recipientLabel =
        concession.recipient === 'tenant_agent' ? 'tenant agent as fee' : 'tenant as rent credit';

    if (concession.format === 'percentage') {
        const pct = concession.percentage ?? 0;
        return `Landlord will pay ${pct}% of one rental payment to ${recipientLabel}.`;
    }

    if (concession.format === 'fixed') {
        const amt = concession.fixed_amount ?? 0;
        return `Landlord will pay ${formatCurrency(amt)} to ${recipientLabel}.`;
    }

    if (concession.format === 'months_rent') {
        const m = concession.months_of_rent ?? 0;
        const pretty =
            Number.isFinite(m) && m === 0.5 ? '½ month' : `${m} month${m === 1 ? '' : 's'}`;
        return `Landlord will pay ${pretty} rent to ${recipientLabel}.`;
    }

    return '';
};

export const buildConcessionDisclosureText = (concession) => {
    if (!concession) return '';

    const recipientPhrase =
        concession.recipient === 'tenant_agent'
            ? "paid to the tenant's agent"
            : concession.recipient === 'tenant'
              ? 'provided to the tenant'
              : 'provided as part of the concession';

    return `This optional concession is offered for the tenant's benefit to reduce the tenant's initial housing cost and make the unit more competitive in the open rental market. The tenant is not required to accept this concession, may decline it, and declining it will not by itself cause the application to be rejected. If the concession is ${recipientPhrase}, the tenant's agent represents only the tenant; this concession does not create an agency relationship between the tenant's agent and the landlord, owner, listing provider, or LeaseHub.`;
};

// export const buildFeesFromDeposits = (deposits = {}) => {
//     const seed = [];

//     // Map your "deposit-like" draft fields into fee rows
//     // Adjust keys here to match what you actually have in propertyInfo / form draft.
//     const mapping = [
//         { key: "securityDeposit", feeType: "securityDeposit", operand: "securityDeposit" },
//         { key: "lastMonthsRent", feeType: "lastMonthsRent", operand: "rent" },
//         // { key: "firstMonthRent", feeType: "firstMonthsRent", operand: "rent" },
//         { key: "brokerFee", feeType: "brokerFee", operand: "rent" },
//         { key: "applicationFee", feeType: "applicationFee", operand: "rent" },
//         { key: "petFee", feeType: "petFee", operand: "petDeposit" }, // optional, change if needed
//         { key: "parkingFee", feeType: "parkingFee", operand: "" }, // optional
//     ];

//     mapping.forEach((m) => {
//         const val = deposits?.[m.key];
//         if (!isValidNumber(val)) return;

//         seed.push({
//             id: `${Date.now()}-${m.key}`,
//             feeType: m.feeType,
//             feeOperandType: m.operand,
//             feeValueType: "flatFee",
//             feeValue: String(Number(val)),
//             feeRangeMin: "",
//             feeRangeMax: "",
//         });
//     });

//     return seed;
// };

export function pad2(n) {
    return String(n).padStart(2, '0');
}

export function toLocalDateInputValue(d) {
    // YYYY-MM-DD in LOCAL time
    const yyyy = d.getFullYear();
    const mm = pad2(d.getMonth() + 1);
    const dd = pad2(d.getDate());
    return `${yyyy}-${mm}-${dd}`;
}

export function toLocalTimeInputValue(d) {
    // HH:MM in LOCAL time
    return `${pad2(d.getHours())}:${pad2(d.getMinutes())}`;
}

export function combineLocalDateTime(dateStr, timeStr) {
    // dateStr: YYYY-MM-DD, timeStr: HH:MM
    if (!dateStr || !timeStr) return null;
    const [y, m, d] = dateStr.split('-').map(Number);
    const [hh, mm] = timeStr.split(':').map(Number);
    return new Date(y, m - 1, d, hh, mm, 0, 0); // local
}

export class PublicApplyError extends Error {
    constructor(message, { status = 0, result = null } = {}) {
        super(message);
        this.name = 'PublicApplyError';
        this.status = status;
        this.result = result;
    }
}

const ACCESS_TOKEN_SESSION_KEY = 'access_link_token';

const appendPublicApplyToken = (url) => {
    const token = typeof sessionStorage !== 'undefined' ? sessionStorage.getItem(ACCESS_TOKEN_SESSION_KEY) : null;
    if (!token) return url;
    const sep = url.includes('?') ? '&' : '?';
    return `${url}${sep}t=${encodeURIComponent(token)}`;
};

export const getPublicApplyListingId = (listing, fallbackPropertyId) =>
    listing?.listing_id || listing?.id || listing?.listingId || fallbackPropertyId || '';

export const hasLandlordProvidedConcession = (property) => {
    if (!property?.concessionsAllowed || !property?.concession) {
        return false;
    }

    const source = property?.concessionSource;
    return !source || source === 'listing' || source === 'account';
};

export const defaultBrokerFeeForLandlordConcession = (moveinDeposits = {}, property) => {
    const deposits = { ...(moveinDeposits || {}) };
    if (hasLandlordProvidedConcession(property)) {
        deposits['Broker Fee'] = 0;
    }
    return deposits;
};

export const startPublicLeadApplication = async ({
    listing,
    propertyId,
    leadId,
    clientId,
    source = 'property_page',
    forceNew = false,
    fetchImpl = fetch,
} = {}) => {
    const listingId = getPublicApplyListingId(listing, propertyId);

    if (!listingId || !clientId) {
        throw new PublicApplyError('Unable to start application for this property.');
    }
    if (!leadId) {
        throw new PublicApplyError('Lead ID is required to start an application.');
    }

    const response = await fetchImpl('/api/application/public/lead/new', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
            propertyId: listingId,
            leadId,
            clientId,
            source,
            forceNew,
        }),
    });

    const result = await response.json().catch(() => ({}));
    if (!response.ok) {
        throw new PublicApplyError(result.message || 'Could not promote application.', {
            status: response.status,
            result,
        });
    }

    return result;
};

export const fetchPublicLeadApplicationContext = async ({
    fetchImpl = fetch,
} = {}) => {
    const response = await fetchImpl(appendPublicApplyToken('/api/application/public/lead/context'), {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' },
    });
    const result = await response.json().catch(() => ({}));
    if (!response.ok) {
        throw new PublicApplyError(result.message || 'Could not load application context.', {
            status: response.status,
            result,
        });
    }
    return result;
};

export const promotePublicLeadApplication = async ({
    payload,
    fetchImpl = fetch,
} = {}) => {
    const response = await fetchImpl(appendPublicApplyToken('/api/application/public/lead/promote'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
    });
    const result = await response.json().catch(() => ({}));
    if (!response.ok) {
        throw new PublicApplyError(result.message || 'Could not start application.', {
            status: response.status,
            result,
        });
    }
    return result;
};
